import { useState, useEffect, useRef } from 'react';
import type { TimeValue } from '@/types';

/**
 * Hook that provides current time with millisecond precision,
 * updated every animation frame for smooth clock hand movement.
 */
export const useTime = (): TimeValue => {
  const [time, setTime] = useState<TimeValue>(() => getCurrentTime());
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const updateTime = () => {
      setTime(getCurrentTime());
      rafRef.current = requestAnimationFrame(updateTime);
    };

    rafRef.current = requestAnimationFrame(updateTime);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return time;
};

/** Get current time as TimeValue */
const getCurrentTime = (): TimeValue => {
  const now = new Date();
  return {
    hours: now.getHours(),
    minutes: now.getMinutes(),
    seconds: now.getSeconds(),
    ms: now.getMilliseconds(),
  };
};
